import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FocusLock from 'react-focus-lock';

import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import { useUserPermissions } from '../../../../common/hooks/use-user-permissions';
import { useAudiences } from '../../../../models/audiences/context';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import { channelSelectors } from '../../../../models/channels';
import { getChannelType } from '../../../content-submission/channels-popover/hooks';
import ChannelsPopover from '../../../content-submission/channels-popover/channels-popover';
import useOutsideClick from '../../../../common/use-outside-click';
import './topic-picker-card.scss';
import classNames from 'classnames';
import { Button, Icon } from '@socialchorus/shared-ui-components';

const POPOVER_ID = 'ugc-topic-picker-popover';

const TopicPickerCard = () => {
  const { t } = useTranslation();
  const {
    userGeneratedContent,
    updateUserGeneratedContentFormState,
    memberValidationErrors,
  } = useUserGeneratedContent();
  const selectedIds = userGeneratedContent.topic_ids ?? [];
  const showError = memberValidationErrors.topic;

  // The audience picker is only reachable for users with publish permission
  // (gates NotificationsCard) when the program also has audiences configured
  // (gates AudiencePicker inside). When either is missing, "audience" isn't
  // a path the user can take to satisfy validation, so the error should say
  // "Topic is required" rather than "Topic and/or audience required".
  const { publishAccess } = useUserPermissions();
  const { audiences: programAudiences } = useAudiences();
  const canSelectAudience = !!publishAccess && programAudiences.length > 0;
  const errorMessageKey = canSelectAudience
    ? 'user_generated_content.validation.topic_or_audience_required'
    : 'user_generated_content.validation.topic_required';

  const allChannels = usePatronSelector(
    channelSelectors.getChannelsWithoutHome
  );
  const selectableChannels = allChannels.filter(
    (c) => getChannelType(c) !== null
  );

  const [isOpen, setIsOpen] = useState(false);

  const handleApply = (ids: number[]) => {
    updateUserGeneratedContentFormState({ topic_ids: ids });
  };

  const containerRef = useOutsideClick({
    disabled: !isOpen,
    onClick: () => setIsOpen(false),
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const selectedChannels = selectableChannels.filter((c) =>
    selectedIds.includes(c.id)
  );

  const triggerButtonLabel =
    selectedChannels.length > 0
      ? selectedChannels.map((c) => c.name).join(', ')
      : t('user_generated_content.select_topics');

  return (
    <div
      className="ugc-topic-picker-card"
      ref={containerRef}
      onKeyDown={handleKeyDown}
    >
      <div className="ugc-topic-picker-card__label text-body">
        {t('user_generated_content.send_to_topic')}
      </div>

      <div className="ugc-topic-picker-card__trigger-area">
        <Button
          label={triggerButtonLabel}
          rightIcon={<Icon>add</Icon>}
          size="compact"
          fullWidth
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={POPOVER_ID}
          variant="outline"
          className={classNames(
            'ugc-topic-picker-card__trigger text-body',
            showError && 'ugc-topic-picker-card__trigger--error'
          )}
          aria-invalid={showError || undefined}
          aria-describedby={showError ? 'ugc-topic-error' : undefined}
          aria-errormessage={showError ? 'ugc-topic-error' : undefined}
          onClick={() => setIsOpen((prev) => !prev)}
        />

        {showError && (
          <div
            id="ugc-topic-error"
            role="alert"
            className="ugc-topic-picker-card__error"
          >
            <Icon size={16}>warning</Icon>
            <span className="text-caption">{t(errorMessageKey)}</span>
          </div>
        )}

        {isOpen && (
          <FocusLock>
            <div className="ugc-topic-picker-card__popover_wrapper">
              <ChannelsPopover
                id={POPOVER_ID}
                suggestedChannels={[]}
                channels={selectableChannels}
                initialSelectedIds={selectedIds}
                onApply={handleApply}
                onClose={() => setIsOpen(false)}
              />
            </div>
          </FocusLock>
        )}
      </div>
    </div>
  );
};

export default TopicPickerCard;
